import Box from 'ext-ui/components/Box/Box'
import Button from 'ext-ui/components/Button/Button'
import Pill from 'ext-ui/components/Pill/Pill'
import React from 'react'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import { useTranslate } from 'src/localization/Trns'
import * as style from 'src/page-components/Accounts/AccountStatementsTab/accountStatementsTab.style'
import StatementList from 'src/page-components/Accounts/AccountStatementsTab/components/StatementList/StatementList'
import StatementPayments from 'src/page-components/Accounts/AccountStatementsTab/components/StatementPayments/StatementPayments'
import { useAccountStatementsData } from 'src/page-components/Accounts/AccountStatementsTab/useAccountStatementsData'
import {
  cardDetailTabSectionHeading,
  cardDetailTabSectionTitle,
} from 'src/page-components/Accounts/cardDetailTabs.style'
import type { CardManagementSource } from 'src/page-components/Accounts/cardManagementSource'

/**
 * `live` and `demo` both render real content; only these two replace it.
 *
 * `loading` deliberately does NOT fall through to the demo figures: painting
 * fabricated balances for a frame and then replacing them is exactly how
 * invented numbers come to look real, and it was visible as a flash of demo
 * data on every open.
 */
const isSectionReady = (state: CardManagementSource): boolean =>
  state === 'live' || state === 'demo'

interface SectionStateProps {
  state: CardManagementSource
  loadingLabel: string
  errorLabel: string
  onRetry: () => void
  /** Prefix for `-loading` / `-error` test ids. */
  testId: string
}

/**
 * The loading and error renders for one section.
 *
 * A component with NAMED props rather than a positional helper: the previous
 * shape took three adjacent strings, so transposing the loading and error copy
 * would have typechecked and silently shown the wrong message. Module scope so
 * it is not redefined on every render of the tab.
 */
const SectionState: React.FC<SectionStateProps> = ({
  state,
  loadingLabel,
  errorLabel,
  onRetry,
  testId,
}) => {
  const translate = useTranslate()

  if (state === 'loading') {
    return (
      <p css={style.message} data-testid={`${testId}-loading`}>
        {loadingLabel}
      </p>
    )
  }

  if (state === 'error') {
    return (
      <div css={style.errorState} data-testid={`${testId}-error`}>
        <p css={style.message} role="alert">
          {errorLabel}
        </p>
        <Button variant="text" onClick={onRetry}>
          {translate('Try again')}
        </Button>
      </div>
    )
  }

  return null
}

interface AccountStatementsTabProps {
  /** The card whose statements to read. */
  creditCardId: string | undefined
  /** Card label for the statement-detail modal, e.g. "T&E Card •• 5678". */
  cardLabel: string
}

/**
 * Statements tab content for the card-detail page (EX-40600).
 *
 * Phase 1 is view-only by design. Payments precedes Statements, the current
 * statement is the first row, and there is no Pay action anywhere — Extend has
 * no bank-payment capability, so that absence is the contract, not a gap. The
 * per-statement Download control is Phase 2 and deliberately not built here.
 *
 * Data arrives through `useAccountStatementsData`, which reads the real
 * Payments and Statements endpoints and falls back to the demo figures PER
 * GROUP for a card either has nothing for. `isDemoPayments` /
 * `isDemoStatements` say which, and drive the per-section "Sample data" badges
 * — read the warning on that hook before enabling this tab anywhere a customer
 * can reach it.
 *
 * Routing stays in `AccountEntityPage` — this is props-only tab content.
 */
const AccountStatementsTab: React.FC<AccountStatementsTabProps> = ({
  creditCardId,
  cardLabel,
}) => {
  const translate = useTranslate()
  // Destructured by group: each carries its own state, so a payments outage
  // changes only the payments section.
  const {
    payments,
    statements,
    paymentsState,
    statementsState,
    retryPayments,
    retryStatements,
  } = useAccountStatementsData(creditCardId)

  // Badged PER SECTION, because the two groups fall back independently — one
  // can be real while the other is not, and a single tab-level badge would
  // either overclaim or underclaim. Nothing else on this page distinguishes
  // fabricated figures from real ones, and the frozen demo dates drift further
  // from today the longer the authoritative endpoints take, so a demo statement
  // reads as a genuine overdue bill. Delete this at the same time as the demo
  // constant, not before.
  const sampleDataBadge = (
    <Pill color="pending" size="small">
      {translate('Sample data')}
    </Pill>
  )

  return (
    // Every sibling tab supplies its own BackgroundPageContent — that is where
    // the page gutters live, so without it this content renders flush to the
    // viewport edges.
    <BackgroundPageContent>
      {/*
        BackgroundPageContent renders only `className` — it does NOT spread the
        rest of its props — so the test id has to live on an element we own.
      */}
      <div css={style.sections} data-testid="accountStatementsTab-content">
        <Box>
          <div css={cardDetailTabSectionHeading}>
            <h2 css={cardDetailTabSectionTitle}>{translate('Payments')}</h2>
            {paymentsState === 'demo' && sampleDataBadge}
          </div>
          {isSectionReady(paymentsState) ? (
            <StatementPayments
              nextPaymentDue={payments.nextPaymentDue}
              payments={payments.history}
            />
          ) : (
            <SectionState
              state={paymentsState}
              loadingLabel={translate('Loading payments…')}
              errorLabel={translate('We couldn’t load payments for this card.')}
              onRetry={retryPayments}
              testId="statementPayments"
            />
          )}
        </Box>

        <Box>
          <div css={cardDetailTabSectionHeading}>
            <h2 css={cardDetailTabSectionTitle}>{translate('Statements')}</h2>
            {statementsState === 'demo' && sampleDataBadge}
          </div>
          {isSectionReady(statementsState) ? (
            <StatementList
              recentStatements={statements.recent}
              olderStatements={statements.older}
              cardLabel={cardLabel}
              creditCardId={creditCardId}
              isDemoStatements={statementsState === 'demo'}
            />
          ) : (
            <SectionState
              state={statementsState}
              loadingLabel={translate('Loading statements…')}
              errorLabel={translate(
                'We couldn’t load statements for this card.',
              )}
              onRetry={retryStatements}
              testId="statementList"
            />
          )}
        </Box>
      </div>
    </BackgroundPageContent>
  )
}

export default AccountStatementsTab
